import { ComputedStagingRoom, StagingConfiguration, StagingUpsellInformationDTO } from 'models/virtualStaging'
import { IdentifiableProduct, ProductIdentifierContext, identifierFor } from './productIdentifier'

import { Nullable } from 'models/helpers'
import { getAPIRoomItemFromStagingRoom } from './getAPIRoomItemFromStagingRoom'

/** Logging coordinates and message wording shared by every identifier this module serializes. */
const IDENTIFIER_CONTEXT: ProductIdentifierContext = {
  flow: 'Gallery staging upsell',
  component: 'galleryStagingPayload',
  action: 'buildStagingUpsellInformation',
}

/** Ids that let a failure event be matched to the order it belongs to. */
export type StagingUpsellCorrelation = {
  stagingAssignmentId?: Nullable<string>
  assignmentId?: Nullable<string>
}

/** Arguments of {@link buildStagingUpsellInformation}. */
interface StagingUpsellInformationInput {
  /** The renovation product being upsold. */
  renovationProduct: IdentifiableProduct
  /** The payment option the client picked, which carries the primary billing product. */
  billingProduct: IdentifiableProduct
  /** Stripe payment intent, absent when the selected payment method does not use Stripe. */
  paymentIntentId?: string
  /** Ids carried into every log this builder emits. */
  correlation?: StagingUpsellCorrelation
}

/**
 * Serializes the rooms of a gallery staging into the `details` map the API expects.
 *
 * Unlike v1 order placement, the gallery flow sends no `productId` per detail: the backend derives
 * the product from the assignment's own staging line (ISSUE-3512). The shared serializer still emits
 * it for v1, so it is stripped here rather than in the serializer.
 */
export const getGalleryStagingDetails = (rooms: ComputedStagingRoom[]): StagingConfiguration =>
  rooms.reduce<StagingConfiguration>((stagingMap, room) => {
    // typeguards
    if (room.roomTypes.isEmpty()) return stagingMap

    const { productId: _legacyProductId, ...roomConfig } = getAPIRoomItemFromStagingRoom(room)

    const imageStyleMap: StagingConfiguration = room.images.reduce((imageMap, { id }) => ({
      ...imageMap,
      [id]: roomConfig,
    }), {})

    return {
      ...stagingMap,
      ...imageStyleMap,
    }
  }, {})

/**
 * Builds the `upsellInformation` block of a gallery staging request, identifying each product by its
 * Product2 code alone.
 *
 * @throws ProductIdentifierError when either product carries no usable product code.
 */
export const buildStagingUpsellInformation = ({
  renovationProduct,
  billingProduct,
  paymentIntentId,
  correlation = {},
}: StagingUpsellInformationInput): StagingUpsellInformationDTO => ({
  ...identifierFor(renovationProduct, 'productCode', 'renovation', { ...IDENTIFIER_CONTEXT, correlation }),
  ...identifierFor(billingProduct, 'primaryBillingProductCode', 'billing', { ...IDENTIFIER_CONTEXT, correlation }),
  stripePaymentIntentId: paymentIntentId ?? '',
})
